import { ArrowUpRight, Check } from "lucide-react";
import Reveal from "@/components/Reveal";

const HIGHLIGHTS = [
  "A full wellbeing & lifestyle assessment",
  "Weekly one-to-one sessions — online or in Dubai",
  "A bio-individual nutrition framework, built for you",
  "Support between sessions, on WhatsApp",
];

const SECONDARY = [
  {
    t: "Foundational Balance",
    dur: "8 weeks",
    d: "Focused support for energy, stress resilience and everyday eating — for when life is full but manageable.",
  },
  {
    t: "Executive Energy Intensive",
    dur: "4 weeks",
    d: "A short, sharp sprint on sleep, focus and fuel, designed for demanding calendars.",
  },
];

export default function Programs() {
  return (
    <section id="programs" className="bg-[#F7F9FC] text-navy py-24" data-testid="programs-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <Reveal>
            <h2 className="font-displaya font-extrabold uppercase text-[clamp(2.6rem,5vw,4.5rem)] leading-[0.95] text-royal">
              Programs designed for you
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-xs text-navy/60">One-to-one only. Every program starts with a free discovery call.</p>
          </Reveal>
        </div>

        <Reveal>
          <div
            data-testid="program-card-next-chapter"
            className="bg-royal text-cream rounded-[2.5rem] p-8 lg:p-14 grid lg:grid-cols-2 gap-10 overflow-hidden relative"
          >
            <div
              aria-hidden="true"
              className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[radial-gradient(circle,rgba(237,242,162,0.18),transparent_65%)]"
            />
            <div className="relative">
              <span className="inline-flex items-center rounded-full bg-butter px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-navy">
                Flagship · 12 weeks · 1-on-1
              </span>
              <h3 className="mt-6 font-displaya font-extrabold uppercase text-[clamp(2.6rem,5vw,4.5rem)] leading-[0.9]">
                Next <span className="text-butter">Chapter</span>
              </h3>
              <p className="mt-6 text-white/85 text-lg leading-relaxed max-w-md">
                My signature twelve-week, one-to-one journey — a full reset for the season you're in.
                We rebuild the way you eat, move, sleep and recover, one doable habit at a time.
              </p>
              <a
                href="#contact"
                data-testid="program-cta-next-chapter"
                className="group mt-8 inline-flex items-center gap-3 rounded-full bg-butter pl-7 pr-2.5 py-2.5 font-semibold text-navy hover:bg-cream transition-colors"
              >
                Ask about Next Chapter
                <span className="grid place-items-center w-10 h-10 rounded-full bg-cream text-navy transition-transform duration-300 group-hover:rotate-45">
                  <ArrowUpRight size={18} />
                </span>
              </a>
            </div>
            <div className="relative bg-white/10 border border-white/15 rounded-3xl p-8 lg:p-10">
              <p className="text-xs uppercase tracking-[0.2em] text-white/60">What's inside</p>
              <ul className="mt-6 space-y-5">
                {HIGHLIGHTS.map((h) => (
                  <li key={h} className="flex items-start gap-3.5">
                    <span className="grid place-items-center w-6 h-6 rounded-full bg-butter text-navy mt-0.5 shrink-0">
                      <Check size={14} strokeWidth={3} />
                    </span>
                    <span className="text-white/90 leading-relaxed">{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {SECONDARY.map((p, i) => (
            <Reveal key={p.t} delay={0.15 + i * 0.1}>
              <div
                data-testid={`program-card-${p.t.toLowerCase().replace(/\s+/g, "-")}`}
                className="h-full bg-white border border-navy/10 rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
              >
                <span className="inline-flex items-center rounded-full bg-royal/5 border border-royal/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-royal">
                  {p.dur} · 1-on-1
                </span>
                <h4 className="mt-5 text-2xl font-semibold">{p.t}</h4>
                <p className="mt-3 text-navy/65 leading-relaxed">{p.d}</p>
                <a
                  href="#contact"
                  data-testid={`program-details-${p.t.toLowerCase().replace(/\s+/g, "-")}`}
                  className="mt-6 inline-flex items-center gap-2 font-semibold text-royal hover:gap-3.5 transition-all"
                >
                  Request details <ArrowUpRight size={17} />
                </a>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
